import {Component, inject, input, OnInit, output, viewChild} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Observable} from 'rxjs';
import {
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewProjectDescriptionUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {RichTextEditorComponent} from '../../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {TabStatus} from '../../../../../../../../shared/tabs/tab-status.model';
import {RichTextSection} from '../../../../../../../../shared/model/rich-text-editor.model';
import {
  ProjectStepRichTextService,
  RichTextSectionConfig,
} from '../../../../../../../../shared/services/project-step-rich-text.service';

@Component({
  selector: 'app-proposal-guidance-preview',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent],
  templateUrl: './proposal-guidance-preview.component.html',
})
export class ProposalGuidancePreviewComponent implements OnInit {
  private readonly invitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );
  readonly projectId = input.required<number>();
  readonly isButtonDisabled = input.required<boolean>();
  readonly preloadedData = input<TenderInvitationPreviewResponse | undefined>(undefined);
  readonly stepLocked = input<boolean>(false);
  readonly statusChange = output<TabStatus>();

  readonly richTextEditor = viewChild(RichTextEditorComponent);

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get mailSections() {
    return this.richTextService.getSections();
  }

  ngOnInit(): void {
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: RFQ_PROPOSAL_GUIDANCE_SECTIONS,
      fieldMap: RFQ_PROPOSAL_GUIDANCE_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) =>
        this.invitationPreviewControllerService.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFQ,
        }),
    };

    this.richTextService.initialize(this.projectId(), config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: TenderInvitationPreviewProjectDescriptionUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    const dto: TenderInvitationPreviewProjectDescriptionUpsertRequest = {
      beginningText: richTextPayload.beginningText,
    };
    return this.invitationPreviewControllerService.upsertProjectDescription({
      projectId,
      phase: TenderPhaseType.RFQ,
      tenderInvitationPreviewProjectDescriptionUpsertRequest: dto,
    });
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();
    this.statusChange.emit({totalSections, completedSections});
  }

  getEmailSubjectForSection(): string {
    return this.preloadedData()?.emailSubject || '';
  }
}

export const RFQ_PROPOSAL_GUIDANCE_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfq_guidance_proposal_preview',
    content: '',
    tooltipKey: '',
    hasChanges: false,
  },
];

export const RFQ_PROPOSAL_GUIDANCE_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewProjectDescriptionUpsertRequest
> = {
  step_rfq_guidance_proposal_preview: 'beginningText',
};
